import { Card, Col, Form, Row, message } from 'antd';
import { useWatch } from 'antd/es/form/Form';
import { FormInstance } from 'antd/lib/form';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useAppSelector } from '../../../../app/hooks';
import { Fixed2 } from '../../../../common/utils/common.utils';
import {
  DateInput,
  FormButton,
  FormInput,
} from '../../../../components/common/FormItem/FormItems';
import {
  SelectClients,
  SelectOptionsInput,
} from '../../../../components/common/FormItem/SelectCustomFeilds';
import { ISubmitHajjRefundData } from '../../../Hajj/Invoice_Hajj/Types/InvoiceHajjTypes';
import {
  useInvoiceHajjGetForRefundDataQuery,
  usePostInvoiceHajjGetForRefundDataMutation,
} from '../../../Hajj/Invoice_Hajj/api/endpoints/invoicehajjEndpoints';
import SelectPaymentTypeAndAccount from '../SelectPaymentTypeAndAccount';
import HajjRefundInputItems from './HajjRefundInputItems';

type Props = {
  form: FormInstance<any>;
  handleCancel?: () => void;
  invoiceId: number;
  invoiceNo: string;
  invoice_category_id: number;
};

const HajjRefundModalContent = ({
  form,
  handleCancel,
  invoiceId,
  invoiceNo,
  invoice_category_id,
}: Props) => {
  const user = useAppSelector((state) => state.user);
  const navigate = useNavigate();

  const [clientAccountSize, setClientAccountSize] = useState(12);
  const [vendorAccountSize, setVendorAccountSize] = useState(12);

  const [postRefund, { isSuccess, isError, isLoading }] =
    usePostInvoiceHajjGetForRefundDataMutation();

  const { data, isSuccess: prevDataSuccess } =
    useInvoiceHajjGetForRefundDataQuery(
      { id: invoiceId! },
      { skip: invoiceId ? false : true }
    );

  // GET DATA FROM APIs
  const ticketInfo = data?.data;

  const formattedTicketInfo =
    ticketInfo?.length &&
    ticketInfo?.map((item) => {
      return {
        pax_name: item.pax_name,
        billing_cost_price: Fixed2(item.billing_cost_price),
        billing_unit_price: item.billing_unit_price,
        comb_vendor: item.comb_vendor,
        billing_id: item.billing_id,
        billing_quantity: item.billing_quantity,
        billing_remaining_quantity: item.billing_remaining_quantity,
        billing_product_id: item.billing_product_id,
        billing_combined_id: item.billing_combined_id,
      };
    });

  useEffect(() => {
    form.setFieldsValue({
      comb_client: ticketInfo?.length && ticketInfo[0]?.comb_client,
      invoice_no: invoiceNo,
      client_refund_type: 'Adjust',
      vendor_refund_type: 'Adjust',
      refund_date: dayjs(),
    });

    form.setFieldValue('billing_info', formattedTicketInfo);
  }, [prevDataSuccess, ticketInfo]);

  const ticketInfos = useWatch('billing_info', form);

  //   CLIENT AND VENDOR TOTAL REFUND AMOUNT
  useEffect(() => {
    if (ticketInfos) {
      form.setFieldsValue({
        client_total_refund: ticketInfos?.reduce(
          (a: any, b: any) => a + Fixed2(b?.client_refund),
          0
        ),
        vendor_total_refund: ticketInfos?.reduce(
          (a: any, b: any) => a + Fixed2(b?.vendor_refund),
          0
        ),
      });
    }
  }, [ticketInfos]);

  const onFinish = (values: ISubmitHajjRefundData) => {
    const body: ISubmitHajjRefundData = {
      ...values,
      created_by: user?.user_id!,
      invoice_id: invoiceId!,
      invoice_category_id: invoice_category_id,
    };

    postRefund(body);
  };

  const onFinishFailed = (errorInfo: any) => {};

  useEffect(() => {
    if (isSuccess) {
      message.success('Refund has been successful');
      form.resetFields();
      navigate(-1);

      handleCancel && handleCancel();
    } else if (isError) {
      message.error('Something happening wrong');
    }
  }, [isSuccess, isError]);

  return (
    <Form
      form={form}
      layout='vertical'
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
    >
      <Card size='small' style={{ width: '100%' }}>
        <Row gutter={20}>
          <SelectClients
            label='Client'
            name={'comb_client'}
            disabled
            size={8}
          />
          <FormInput
            label='Invoice No.'
            name={'invoice_no'}
            readonly
            size={8}
          />

          <DateInput
            label='Refund Date.'
            name={'refund_date'}
            size={8}
            required
          />

          {/* Nest Form.List */}
          <Col lg={24}>
            <Form.Item>
              <Form.List name={['billing_info']}>
                {(subFields, subOpt) => (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      rowGap: 12,
                    }}
                  >
                    {subFields?.map((subField, index) => (
                      <HajjRefundInputItems
                        key={index}
                        subField={subField}
                        subOpt={subOpt}
                        form={form}
                      />
                    ))}
                  </div>
                )}
              </Form.List>
            </Form.Item>
          </Col>

          {/* CLIENT REFUND INFORMATION */}
          <FormInput
            label='Total Client Refund'
            name={'client_total_refund'}
            size={clientAccountSize}
          />
          <SelectOptionsInput
            size={clientAccountSize}
            options={['Adjust', 'Return']}
            label='Client Refund Type'
            name={'client_refund_type'}
            onSelect={(value) =>
              setClientAccountSize(value === 'Adjust' ? 12 : 6)
            }
          />
          {clientAccountSize === 6 && (
            <>
              <SelectPaymentTypeAndAccount
                size={clientAccountSize}
                form={form}
                payMethodName='client_payment_method'
                accountName='client_payment_acc_id'
              />
            </>
          )}

          {/* VENDOR REFUND INFORMATION */}
          <FormInput
            name={'vendor_total_refund'}
            label='Total Vendor Refund'
            size={vendorAccountSize}
          />
          <SelectOptionsInput
            size={vendorAccountSize}
            options={['Adjust', 'Return']}
            label='Vendor Refund Type'
            name={'vendor_refund_type'}
            onSelect={(value) =>
              setVendorAccountSize(value === 'Adjust' ? 12 : 6)
            }
          />
          {vendorAccountSize === 6 && (
            <>
              <SelectPaymentTypeAndAccount
                size={vendorAccountSize}
                form={form}
                payMethodName='vendor_payment_method'
                accountName='vendor_payment_acc_id'
              />
            </>
          )}
        </Row>
        <FormButton label='Submit' loading={isLoading} />
      </Card>
    </Form>
  );
};

export default HajjRefundModalContent;
